import React from 'react';
import { Droppable } from '@hello-pangea/dnd';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';

// Module Components
import FieldRow from './FieldRow';

// Constants
import { RAIL_DND_TYPE, RAIL_DROPPABLE_ID, STRINGS } from '../../../constants';

// Types
import { FieldDefinition, FieldId } from '../../../types';

interface FieldListProps {
  fields: FieldDefinition[];
  enabledFieldIds: FieldId[];
  /**
   * The field whose properties panel is currently open — the one row that carries
   * the selected chrome (spec R79). Deliberately a separate value from
   * `focusReturnFieldId`: that one names the row focus is owed *after* the panel
   * closed, which is the opposite moment.
   */
  inspectedFieldId: FieldId | null;
  /** The field whose row should take focus back after the panel closed. */
  focusReturnFieldId: FieldId | null;
  onToggleField: (fieldId: FieldId, enabled: boolean) => void;
  onOpenProperties: (fieldId: FieldId) => void;
  onFocusReturned: () => void;
}

/**
 * The pane's field list: every available field, searchable, each row with a
 * checkbox and reorderable by its grip (spec R36, R40, R41, R47, R60).
 *
 * Disabled fields stay in the list — only the checkbox state changes — so a field
 * can always be ticked back on (spec R41).
 *
 * The list is the pane's one scroll container, which is also what keeps scrolling
 * it from moving the preview (spec R59, R74). Deliberately one container and not two:
 * a droppable nested inside two scrolling ancestors confuses the drag library's
 * measurements.
 *
 * Its height comes from the pane, not from the viewport. While the pane is a bounded
 * column (`md:` and up) the list fills what the sections above it leave and scrolls
 * inside that; the `50vh` cap is what remains below `md:`, where the pane is a block
 * in a scrolling page and there is no column height to fill. A viewport fraction is
 * the wrong bound in the column case — it knows nothing about the 60px application
 * header or the pane's own chrome, so it either clips or never engages (spec R74).
 */
const FieldList = ({
  fields,
  enabledFieldIds,
  inspectedFieldId,
  focusReturnFieldId,
  onToggleField,
  onOpenProperties,
  onFocusReturned,
}: FieldListProps) => {
  if (fields.length === 0) {
    return (
      <div className="tw-px-3 tw-py-4">
        <Typography variant="body-primary" as="p" className="tw-text-ink-tertiary">
          {STRINGS.pane.noFieldMatches}
        </Typography>
      </div>
    );
  }

  return (
    <Droppable droppableId={RAIL_DROPPABLE_ID} type={RAIL_DND_TYPE}>
      {(provided) => (
        // A plain container rather than a list: the library renders its drop
        // placeholder as a sibling of the rows, which would not be valid inside
        // a <ul>.
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className="tw-max-h-[50vh] md:tw-max-h-none md:tw-flex-1 md:tw-min-h-0 tw-overflow-y-auto tw-px-2 tw-py-2"
        >
          {fields.map((definition, index) => (
            <FieldRow
              key={definition.id}
              definition={definition}
              index={index}
              isEnabled={enabledFieldIds.includes(definition.id)}
              isSelected={inspectedFieldId === definition.id}
              shouldReturnFocus={focusReturnFieldId === definition.id}
              onToggle={(enabled) => onToggleField(definition.id, enabled)}
              onOpenProperties={() => onOpenProperties(definition.id)}
              onFocusReturned={onFocusReturned}
            />
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};

export default FieldList;
